import { createContext, useCallback, useContext, useEffect, useState } from "react";
import type {
  UserInterface,
} from "../interface";

import { postResource } from "../services/api";

// Types
export interface AuthState {
  user: UserInterface | null;
  role: 'admin' | 'coordinateur regional' | 'coordinateur prefectoral' | 'coordinateur sous-prefectoral' | 'relais paysans pour la victoire (VDP)' | null;
  prefectureCount: number;

  isAuthenticated: boolean;
  isLoading: boolean;
}

export const AuthContext = createContext<{
  authState: AuthState;

  login: (data: any) => Promise<boolean>;

  verifyPhone: (code: string) => Promise<boolean>;

  logout: () => Promise<boolean>;

  checkAuth: () => Promise<boolean>;
} | null>(null);

// =========================
//   MAIN AUTH PROVIDER
// =========================
export const useAuth = () => {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    role: null,

    isAuthenticated: false,
    isLoading: true,
    prefectureCount: 0,
  });

  // Vérifier l'auth automatiquement au chargement
  useEffect(() => {
    checkAuth();
  }, []);

  // =========================
  //   CHECK AUTH
  // =========================
  const checkAuth = useCallback(async (): Promise<boolean> => {
    try {
      const formData = new FormData();
      const response = await postResource("get-me", formData);

      if (response.status === 200) {
        const data = response.data;
        setAuthState({
          user: data.user,
          role: data.role,
          prefectureCount: data.prefecture,
          isAuthenticated: true,
          isLoading: false,
        });
        return true;
      }
    } catch (e) {
      console.log('data-error', e);
      setAuthState((prev) => ({
        ...prev,
        isAuthenticated: false,
        isLoading: false,
        user: null,
        role: null,
      }));
    }
    return false;
  }, []);


  // =========================
  //   LOGIN (sans API)
  // =========================
  const login = async (data: any): Promise<boolean> => {
    try {
      setAuthState((prev) => ({ ...prev, isLoading: true }));

      // Mise à jour de l'état global
      setAuthState({
        user: data.user ?? null,
        role: data.role ?? null,
        prefectureCount: data.prefecture ?? 0,

        isAuthenticated: true,
        isLoading: false,
      });
      return true;
    } catch (e) {
      console.error("Erreur login :", e);

      setAuthState((prev) => ({
        ...prev,
        isAuthenticated: false,
        isLoading: false,
      }));
      return false;
    }
  };

  // =========================
  //   VERIFY PHONE
  // =========================
  const verifyPhone = async (code: string): Promise<boolean> => {
    try {
      const formData = new FormData();
      formData.append("code", code);
      formData.append("telephone", authState.user ? authState.user.telephone : '')

      const response = await postResource("auth/verify-otp", formData);

      if (response.status === 200) {
        await checkAuth();
        return true;
      }
    } catch (e) {
      console.error("Erreur verification:", e);
    }

    return false;
  };

  // =========================
  //   LOGOUT
  // =========================
  const logout = async (): Promise<boolean> => {
    try {
      const response = await postResource("logout", new FormData());
      if(response.status === 200){
        console.log('logout');
        localStorage.clear();
        return true;
      }
    } catch (e) {
      console.error("Logout error:", e);
    }
    // setAuthToken(null);
    setAuthState({
      user: null,
      role: null,
      prefectureCount: 0,

      isAuthenticated: false,
      isLoading: false,
    });

    return false
  };

  return {
    authState,
    login,
    verifyPhone,
    logout,
    checkAuth,
  };
};

// ===============
// HOOK CONTEXT
// ===============
export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (!context)
    throw new Error("useAuthContext doit être utilisé dans un AuthProvider");
  return context;
};
